// @ai-generated 2026-07-03T19:05:00Z by dimitri.driediger@synqony.com using Claude Fable 5
import {
    Button,
    Card,
    FlexRow,
    Grid,
    GridColumn,
    GridColumns,
    iconSizeMd,
    Index,
    Modal,
    ModalIconTitle,
    TextInputBase,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconAlertCircle, IconMapPin } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import classes from './NewAddressModal.module.css';
import { type SiteContactDraft, SiteContactFields } from './SiteContactFields';

export interface NewAddressDraft {
    label: string;
    street: string;
    houseNumber: string;
    zipCode: string;
    city: string;
    /** Optional access hint ("Zufahrtshinweis"); ends up in the requisition remarks. */
    accessHint: string;
    /** Optional on-site contact captured alongside the new address. */
    contactFirstName: string;
    contactLastName: string;
    contactPhone: string;
}

interface NewAddressModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Called with the validated draft. The caller persists it as site snapshot and closes the modal. */
    onSave: (draft: NewAddressDraft) => void;
}

const emptyDraft: NewAddressDraft = {
    label: '',
    street: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    accessHint: '',
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
};

/**
 * Modal captures a new site address (e.g. a construction
 * site) as an address snapshot. Required fields are marked
 * with a red border after the first failed save attempt.
 */
export function NewAddressModal({ isOpen, onClose, onSave }: NewAddressModalProps) {
    const i18n = useTranslate('');
    const [draft, setDraft] = useState<NewAddressDraft>(emptyDraft);
    const [hasTriedToSave, setHasTriedToSave] = useState(false);

    const updateField = (key: Index<NewAddressDraft>) => (value: string) => {
        setDraft((previous) => ({ ...previous, [key]: value }));
        return true;
    };

    const isMissing = (value: string) => hasTriedToSave && !value.trim();
    const isValid =
        draft.label.trim() &&
        draft.street.trim() &&
        draft.houseNumber.trim() &&
        draft.zipCode.trim() &&
        draft.city.trim();

    const reset = useCallback(() => {
        setDraft(emptyDraft);
        setHasTriedToSave(false);
    }, [setDraft, setHasTriedToSave]);

    const close = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    const save = useCallback(() => {
        if (!isValid) {
            setHasTriedToSave(true);
            return;
        }
        onSave({
            label: draft.label.trim(),
            street: draft.street.trim(),
            houseNumber: draft.houseNumber.trim(),
            zipCode: draft.zipCode.trim(),
            city: draft.city.trim(),
            accessHint: draft.accessHint.trim(),
            contactFirstName: draft.contactFirstName.trim(),
            contactLastName: draft.contactLastName.trim(),
            contactPhone: draft.contactPhone.trim(),
        });
    }, [isValid, draft, setHasTriedToSave, onSave]);

    const onChangeContact = useCallback(
        (patch: Partial<SiteContactDraft>) =>
            setDraft((previous) => {
                const next = { ...previous };
                if (patch.firstName !== undefined) {
                    next.contactFirstName = patch.firstName;
                }
                if (patch.lastName !== undefined) {
                    next.contactLastName = patch.lastName;
                }
                if (patch.phone !== undefined) {
                    next.contactPhone = patch.phone;
                }
                return next;
            }),
        [setDraft],
    );

    const title = (
        <ModalIconTitle
            icon={<IconMapPin size={iconSizeMd} aria-hidden="true" />}
            title={i18n('Neue Anschrift anlegen')}
            subtitle={i18n('z. B. eine neue Baustelle als Leistungsort')}
        />
    );

    const footer = (
        <FlexRow justify="space-between">
            <Button appearance="outlined" variant="normal" onClick={close} label={i18n('Abbrechen')}>
                {i18n('Abbrechen')}
            </Button>
            <Button
                appearance="filled"
                variant="primary"
                onClick={save}
                label={i18n('Anschrift speichern & auswählen')}
            >
                {i18n('Speichern & auswählen')}
            </Button>
        </FlexRow>
    );

    return (
        <Modal isOpen={isOpen} onClose={close} title={title} footer={footer}>
            <Card name="address" collapsable={false} border="withoutBorder">
                <Grid columns={12}>
                    <GridColumn span={12}>
                        <TextInputBase
                            name="new_address_label"
                            label={i18n('Bezeichnung')}
                            placeholder={i18n('z. B. Baustelle Nordring')}
                            value={draft.label}
                            onChange={updateField('label')}
                            valid={!isMissing(draft.label)}
                            autoFocusOnRender
                        />
                    </GridColumn>
                    <GridColumns spans={[8, 4]}>
                        <TextInputBase
                            name="new_address_street"
                            label={i18n('Straße')}
                            placeholder={i18n('z. B. Nordring')}
                            value={draft.street}
                            onChange={updateField('street')}
                            valid={!isMissing(draft.street)}
                        />
                        <TextInputBase
                            name="new_address_house_number"
                            label={i18n('Hausnummer')}
                            value={draft.houseNumber}
                            onChange={updateField('houseNumber')}
                            valid={!isMissing(draft.houseNumber)}
                        />
                    </GridColumns>

                    <GridColumns spans={[3, 9]}>
                        {/* A single space suppresses the fallback to the label as placeholder — the fields stay visually empty. */}
                        <TextInputBase
                            name="new_address_zip"
                            label={i18n('PLZ')}
                            value={draft.zipCode}
                            onChange={updateField('zipCode')}
                            valid={!isMissing(draft.zipCode)}
                        />
                        <TextInputBase
                            name="new_address_city"
                            label={i18n('Ort')}
                            value={draft.city}
                            onChange={updateField('city')}
                            valid={!isMissing(draft.city)}
                        />
                    </GridColumns>

                    <GridColumn span={12}>
                        <TextInputBase
                            name="new_address_access_hint"
                            label={`${i18n('Zufahrtshinweis')} (${i18n('optional')})`}
                            placeholder={i18n('z. B. Einfahrt über Tor 3, max. 12 t')}
                            value={draft.accessHint}
                            inputSize={3}
                            onChange={updateField('accessHint')}
                        />
                    </GridColumn>
                </Grid>
            </Card>
            {/* Zugeklappt ist diese Karte nur eine Zeile und stösst sonst direkt an die Buttons
                des Dialogs — der Abstand sagt, dass sie zum Formular gehört, nicht zum Fuss. */}
            <Card
                name="contact"
                label={`${i18n('Ansprechpartner vor Ort')} (${i18n('optional')})`}
                margin={{ bottom: 'sm' }}
                closed
            >
                <SiteContactFields
                    idPrefix="new_address_contact"
                    value={{
                        firstName: draft.contactFirstName,
                        lastName: draft.contactLastName,
                        phone: draft.contactPhone,
                    }}
                    onChange={onChangeContact}
                />
            </Card>
            {hasTriedToSave && !isValid && (
                <div className={classes.error}>
                    <IconAlertCircle size={iconSizeMd} aria-hidden="true" />
                    {i18n('Bitte fülle Bezeichnung, Straße, Hausnummer, PLZ und Ort aus.')}
                </div>
            )}
        </Modal>
    );
}
